import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from './SubmitButton';
import { loginWithMfa } from './api';
import { useFormSubmit } from './useFormSubmit';

interface MfaState {
  session: string;
  challengeName: string;
  destination: string | null;
  email: string;
}

interface Props {
  mfaState: MfaState;
  onBack: () => void;
  /** Language deliberately picked on this page, if any. */
  chosenLang?: string;
}

export const MfaForm = ({ mfaState, onBack, chosenLang }: Props) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const { loading, error, setError, runSubmit } = useFormSubmit();

  const isSms = mfaState.challengeName === 'SMS_MFA';

  let description: string;
  if (!isSms) {
    description = t('login.mfa.totpDescription');
  } else if (mfaState.destination) {
    description = t('login.mfa.smsSentTo', { destination: mfaState.destination });
  } else {
    description = t('mfa.form.smsMessage');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSubmit(async () => {
      const result = await loginWithMfa({
        session: mfaState.session,
        code,
        challengeName: mfaState.challengeName,
        email: mfaState.email,
        lang: chosenLang,
      });
      if (result.status === 'OK') {
        globalThis.location.href = '/';
      }
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          id="mfa-code"
          label={isSms ? t('mfa.form.smsCode') : t('mfa.form.authenticatorCode')}
          value={code}
          onChange={(e) => setCode(e.target.value.trim())}
          required
          autoComplete="one-time-code"
          autoFocus
          fullWidth
          size="small"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 8 }}
        />

        <SubmitButton loading={loading} disabled={!code}>
          {t('login.mfa.verify')}
        </SubmitButton>

        <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: 'center' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    </Box>
  );
};
